import { Field } from '@tm/shared-lib/src/field'
import { useCallback, useEffect, useState } from 'react'
import { useSurveyValue } from '../../../../hooks/useSurveyValue'
import { ViewProps } from '../types'
import { NpsQuestionView } from './NpsQuestionView'
import { useTitle } from '@tm/client-form/src/hooks/useTitle'

/**
 * Nps question loader will check how NpsQuestionView should be rendered
 * and updates Survey context accordingly
 */

type NpsQuestionLoaderInnerProps = {
  field: Field
  lang: string
  submitForm: () => void
  isSubmitting: boolean
}

function NpsQuestionLoaderInner({ field, lang, submitForm, isSubmitting }: NpsQuestionLoaderInnerProps) {
  const [value, setValue] = useSurveyValue(field)
  const [shouldSubmit, setShouldSubmit] = useState(false)
  const [showSubmit, setShowSubmit] = useState(false)
  const label = 'label' in field && field.label ? field.label[lang] : undefined

  useTitle(label)

  const onNpsChange = useCallback(
    (nextValue: number | null) => {
      setValue(nextValue)
      if (showSubmit) return
      setShouldSubmit(true)
    },
    [setValue, showSubmit]
  )

  /**
   * Submit form if value is set and user has given a value
   * With immer it's hard to set value and submit same time,
   * so we listen when value is set and then submit.
   *
   * We use shouldSubmit to wait user to interact with the form,
   * otherwise when user returns to form, this would submit form
   * immediately
   */
  useEffect(() => {
    if (shouldSubmit && typeof value === 'number') {
      submitForm()
    } else if (typeof value === 'number') {
      // Is user lands with value, we want to show submit button to let user know how to go forward
      setShowSubmit(true)
    }
  }, [value, submitForm, shouldSubmit])

  return (
    <NpsQuestionView
      value={typeof value === 'number' ? value : undefined}
      label={label}
      onChange={onNpsChange}
      name={field.name}
      disabled={isSubmitting}
      showSubmit={showSubmit}
      onSubmit={() => setShouldSubmit(true)}
    />
  )
}

export function NpsQuestionLoader({ state, submitForm, isSubmitting }: ViewProps) {
  const fields = state.step.fields || []
  const npsField = fields.find(field => field.type === 'nps')
  if (!npsField) return null
  return (
    <NpsQuestionLoaderInner field={npsField} lang={state.lang} submitForm={submitForm} isSubmitting={isSubmitting} />
  )
}
